// Permission catalog + example roles for the admin-restriction framework.
//
// STEP 1 (framework + test harness): roles are defined here, client-side, and
// the "real" role is full-access for everyone — so nothing changes for normal
// users yet. A super-user can SIMULATE a role (see Permissions.js) to preview
// what a restricted user would see. A later step moves role definitions +
// per-user assignment to the backend (new per-user permission model) and a
// management UI; the `can(key)` call sites built now stay unchanged.
//
// Permission keys are dot-namespaced: `<area>.<kind>.<name>`.
//   - `<area>.screen.<x>` → a whole tab/screen (coarse gating)
//   - `<area>.field.<x>`  → a specific field/section (fine gating)
//
// The CATALOG of gateable nodes is now derived dynamically from the search
// index (see permissionCatalog.js) — the role-config dialog reads that, not the
// flat PERMISSION_GROUPS below (kept only as a label reference for now). The
// example ROLES are seeds: editable + persisted per browser, replaced by a
// backend per-user model later.

import { ALL_CATALOG_KEYS, subtreeKeys } from './permissionCatalog';

export const PERMISSION_GROUPS = [
    {
        key: 'webshop',
        label: 'Webshop',
        permissions: [
            { key: 'webshop.screen.config',       label: 'Tab: Configuratie' },
            { key: 'webshop.screen.data',         label: 'Tab: Data' },
            { key: 'webshop.screen.connections',  label: 'Tab: Connecties' },
            { key: 'webshop.screen.actions',      label: 'Tab: Acties' },
            // Sub-tabs within the Configuratie screen.
            { key: 'webshop.config.tab.pages',      label: 'Configuratie › Pagina’s' },
            { key: 'webshop.config.tab.style',      label: 'Configuratie › Opmaak' },
            { key: 'webshop.config.tab.features',   label: 'Configuratie › Functies' },
            { key: 'webshop.config.tab.processing', label: 'Configuratie › Verwerking' },
            { key: 'webshop.config.tab.sync',       label: 'Configuratie › Synchronisatie' },
            { key: 'webshop.field.dbCredentials', label: 'Veld: Database-credentials (Connecties)' },
        ],
    },
];

export const ALL_PERMISSION_KEYS = PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.key));

// Example roles. `allow: '*'` = everything; otherwise an explicit allow-list.
// These are placeholders to demonstrate tab-level AND field-level gating until
// the backend per-user model lands.
export const ROLES = {
    owner: {
        label: 'Volledige toegang',
        allow: '*',
    },
    manager: {
        // Everything in the catalog except the DB-credentials field. Computed
        // from the catalog, so newly-added cards are automatically included.
        label: 'Beheerder (geen DB-credentials)',
        allow: ALL_CATALOG_KEYS.filter(k => k !== 'webshop.field.dbCredentials'),
    },
    editor: {
        // The Configuratie screen + the Pages & Style tabs and everything under
        // them (cards included via subtreeKeys). Other tabs/screens stay hidden.
        label: 'Content (Configuratie: enkel Pagina’s + Opmaak)',
        allow: [
            'webshop.screen.config',
            ...subtreeKeys('webshop.config.tab.pages'),
            ...subtreeKeys('webshop.config.tab.style'),
        ],
    },
};

// The role assigned to a user when the backend hasn't defined one yet. Full
// access on purpose — gating only kicks in once roles are really assigned (or
// while a super-user is simulating).
export const DEFAULT_ROLE = 'owner';

// A stable accent colour per role key — quick visual distinction in the manager
// list and the simulate widget. Owner is gold (signals full access).
const ROLE_PALETTE = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#0288d1', '#c2185b', '#00897b', '#5e35b1'];
export const roleColor = (key) => {
    if (key === 'owner') return '#b8860b';
    let h = 0;
    const s = String(key);
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return ROLE_PALETTE[h % ROLE_PALETTE.length];
};
